import { getCategory, pickPuzzle as defaultPickPuzzle } from './categories';
import {
  cardsAreUnique,
  isValidCard,
  opponentHitsAreLegal,
  tableOutcome,
} from './table';
import {
  AvailableActions,
  EngineDeps,
  GameAction,
  GameState,
  MAX_PLAYERS,
  MAX_ROUNDS,
  MIN_CASH_SPIN,
  MIN_ROUNDS,
  Player,
  SLOT_SPECIALS,
  SpecialReel,
  SpinResult,
  TABLE_PUSH_IT_MIN,
  TableAgainst,
  TableCard,
  TableChallenge,
  VOWEL_COST,
  VOWELS,
} from './types';

export {
  againstLabel,
  cardKey,
  cardsAreUnique,
  dealTableHands,
  drawTableHits,
  fullDeck,
  handTotal,
  isBust,
  isValidCard,
  opponentHitsAreLegal,
  tableOutcome,
} from './table';

export class GameError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GameError';
  }
}

export function isVowel(letter: string): boolean {
  return (VOWELS as readonly string[]).includes(letter.toLowerCase());
}

export function isConsonant(letter: string): boolean {
  const normalized = letter.toLowerCase();
  return /^[a-z]$/.test(normalized) && !isVowel(normalized);
}

export function normalizeLetter(letter: string): string {
  const normalized = letter.trim().toLowerCase();
  if (!/^[a-z]$/.test(normalized)) {
    throw new GameError('Guess a single letter A–Z');
  }
  return normalized;
}

export function uniqueLettersInPhrase(phrase: string): string[] {
  const letters = new Set<string>();
  for (const char of phrase.toLowerCase()) {
    if (/[a-z]/.test(char)) {
      letters.add(char);
    }
  }
  return Array.from(letters);
}

export function countLetterInPhrase(phrase: string, letter: string): number {
  const target = letter.toLowerCase();
  let count = 0;
  for (const char of phrase.toLowerCase()) {
    if (char === target) {
      count += 1;
    }
  }
  return count;
}

export function isBoardComplete(phrase: string, guessedLetters: string[]): boolean {
  const guessed = new Set(guessedLetters.map((letter) => letter.toLowerCase()));
  return uniqueLettersInPhrase(phrase).every((letter) => guessed.has(letter));
}

export function normalizeSolveGuess(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, ' ');
}

export function generateSpin(random: () => number = Math.random): SpinResult {
  const reelValues: [number, number, number, number] = [
    Math.floor(random() * 10),
    Math.floor(random() * 10),
    Math.floor(random() * 10),
    Math.floor(random() * 10),
  ];
  const specialValue = SLOT_SPECIALS[Math.floor(random() * SLOT_SPECIALS.length)] as SpecialReel;
  const rawValue = reelValues.reduce((total, digit) => total * 10 + digit, 0);
  return {
    reelValues,
    specialValue,
    cashValue: Math.max(MIN_CASH_SPIN, rawValue),
  };
}

export function isValidSpin(spin: SpinResult): boolean {
  if (!spin || !Array.isArray(spin.reelValues) || spin.reelValues.length !== 4) {
    return false;
  }
  if (spin.reelValues.some((digit) => !Number.isInteger(digit) || digit < 0 || digit > 9)) {
    return false;
  }
  if (!(SLOT_SPECIALS as readonly string[]).includes(spin.specialValue)) {
    return false;
  }
  return Number.isInteger(spin.cashValue) && spin.cashValue >= MIN_CASH_SPIN;
}

export function currentPlayer(state: GameState): Player {
  const player = state.players[state.currentPlayerIndex];
  if (!player) {
    throw new GameError('No current player');
  }
  return player;
}

export function getAvailableActions(state: GameState, playerId: string): AvailableActions {
  const me = state.players.find((player) => player.id === playerId);
  const isTurn = Boolean(me) && currentPlayerSafe(state)?.id === playerId;
  const playing = state.status === 'playing';
  const tableOffered = Boolean(state.tableOffered);
  const tableUsed = Boolean(state.tableUsedThisTurn);

  return {
    isMyTurn: isTurn,
    isHost: Boolean(me?.isHost),
    canSpin: playing && isTurn && state.phase === 'awaiting_action',
    canGuessConsonant: playing && isTurn && state.phase === 'awaiting_consonant',
    canBuyVowel: playing && isTurn && state.phase === 'awaiting_action' && (me?.roundScore ?? 0) >= VOWEL_COST,
    canSolve: playing && isTurn && state.phase === 'awaiting_action',
    canEndGame: Boolean(me?.isHost) && state.status !== 'finished',
    canAdvance: playing && state.phase === 'round_complete',
    canPushIt:
      playing &&
      isTurn &&
      state.phase === 'awaiting_consonant' &&
      tableOffered &&
      !tableUsed &&
      (state.currentSpinValue ?? 0) >= TABLE_PUSH_IT_MIN,
    canTableChoose: playing && isTurn && state.phase === 'awaiting_table_choice',
    canTableAct: playing && isTurn && state.phase === 'awaiting_table',
  };
}

function currentPlayerSafe(state: GameState): Player | undefined {
  return state.players[state.currentPlayerIndex];
}

export function createInitialGame(params: {
  id: string;
  hostId: string;
  hostName: string;
  now?: number;
}): GameState {
  const now = params.now ?? Date.now();
  const hostName = params.hostName.trim() || 'Host';

  return {
    id: params.id,
    hostId: params.hostId,
    playerIds: [params.hostId],
    players: [
      {
        id: params.hostId,
        name: hostName,
        roundScore: 0,
        totalScore: 0,
        isHost: true,
      },
    ],
    status: 'lobby',
    phase: 'lobby',
    totalRounds: 3,
    currentRound: 0,
    selectedCategoryIds: [],
    usedPuzzles: [],
    currentPhrase: '',
    currentCategory: '',
    currentPlayerIndex: 0,
    currentSpinValue: null,
    lastSpin: null,
    lastSpinAt: null,
    table: null,
    tableOffered: false,
    tableUsedThisTurn: false,
    guessedLetters: [],
    message: `${hostName} created the game. Share the code so others can join.`,
    winners: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function applyAction(state: GameState, action: GameAction, deps: EngineDeps = {}): GameState {
  const now = deps.now ?? Date.now();
  const hydrated = hydrateTableFields(state);

  switch (action.type) {
    case 'JOIN':
      return touch(joinGame(hydrated, action.playerId, action.playerName), now);
    case 'SET_CONFIG':
      return touch(setConfig(hydrated, action.playerId, action.totalRounds, action.selectedCategoryIds), now);
    case 'START':
      return touch(startGame(hydrated, action.playerId, deps), now);
    case 'SPIN':
      return touch(spinSlot(hydrated, action.playerId, action.spin, now), now);
    case 'TABLE_START':
      return touch(
        startTable(hydrated, action.playerId, action.against, action.playerHand, action.opponentHand),
        now
      );
    case 'TABLE_RESOLVE':
      return touch(resolveTable(hydrated, action.playerId, action.playerHit, action.opponentHits), now);
    case 'GUESS_CONSONANT':
      return touch(guessConsonant(hydrated, action.playerId, action.letter), now);
    case 'BUY_VOWEL':
      return touch(buyVowel(hydrated, action.playerId, action.letter), now);
    case 'SOLVE':
      return touch(solvePhrase(hydrated, action.playerId, action.guess), now);
    case 'ADVANCE_ROUND':
      return touch(advanceRound(hydrated, deps), now);
    case 'END_GAME':
      return touch(endGame(hydrated, action.playerId), now);
    default:
      throw new GameError('Unknown action');
  }
}

function hydrateTableFields(state: GameState): GameState {
  return {
    ...state,
    table: state.table ?? null,
    tableOffered: Boolean(state.tableOffered),
    tableUsedThisTurn: Boolean(state.tableUsedThisTurn),
  };
}

function touch(state: GameState, now: number): GameState {
  return { ...state, updatedAt: now };
}

function requireHost(state: GameState, playerId: string): void {
  const player = state.players.find((entry) => entry.id === playerId);
  if (!player?.isHost) {
    throw new GameError('Only the host can do that');
  }
}

function requireTurn(state: GameState, playerId: string): Player {
  if (state.status !== 'playing') {
    throw new GameError('The game is not in progress');
  }
  const player = currentPlayer(state);
  if (player.id !== playerId) {
    throw new GameError("It's not your turn");
  }
  return player;
}

function clonePlayers(players: Player[]): Player[] {
  return players.map((player) => ({ ...player }));
}

function nextIndex(state: GameState): number {
  return (state.currentPlayerIndex + 1) % state.players.length;
}

function passTurn(state: GameState, message: string): GameState {
  const next = nextIndex(state);
  return {
    ...state,
    players: clonePlayers(state.players),
    currentPlayerIndex: next,
    phase: 'awaiting_action',
    currentSpinValue: null,
    tableOffered: false,
    tableUsedThisTurn: false,
    message: `${message} It's ${state.players[next].name}'s turn.`,
  };
}

function joinGame(state: GameState, playerId: string, playerName: string): GameState {
  const existing = state.players.find((player) => player.id === playerId);
  if (existing) {
    const name = playerName.trim() || existing.name;
    return {
      ...state,
      players: state.players.map((player) =>
        player.id === playerId ? { ...player, name } : player
      ),
    };
  }

  if (state.status !== 'lobby') {
    throw new GameError('This game has already started');
  }
  if (state.players.length >= MAX_PLAYERS) {
    throw new GameError(`This game is full (${MAX_PLAYERS} players max)`);
  }

  const name = playerName.trim() || `Player ${state.players.length + 1}`;
  const player: Player = {
    id: playerId,
    name,
    roundScore: 0,
    totalScore: 0,
    isHost: false,
  };

  return {
    ...state,
    players: [...clonePlayers(state.players), player],
    playerIds: [...state.playerIds, playerId],
    message: `${name} joined the game.`,
  };
}

function setConfig(
  state: GameState,
  playerId: string,
  totalRounds: number,
  selectedCategoryIds: string[]
): GameState {
  requireHost(state, playerId);
  if (state.status !== 'lobby') {
    throw new GameError('Settings can only be changed in the lobby');
  }
  if (!Number.isInteger(totalRounds) || totalRounds < MIN_ROUNDS || totalRounds > MAX_ROUNDS) {
    throw new GameError(`Choose between ${MIN_ROUNDS} and ${MAX_ROUNDS} rounds`);
  }
  if (selectedCategoryIds.length > totalRounds) {
    throw new GameError('Too many categories for the number of rounds');
  }

  return {
    ...state,
    totalRounds,
    selectedCategoryIds: [...selectedCategoryIds],
  };
}

function startGame(state: GameState, playerId: string, deps: EngineDeps): GameState {
  requireHost(state, playerId);
  if (state.status !== 'lobby') {
    throw new GameError('This game has already started');
  }
  if (state.players.length < 1) {
    throw new GameError('At least one player is required');
  }
  if (state.selectedCategoryIds.length !== state.totalRounds) {
    throw new GameError(`Select a category for each of the ${state.totalRounds} rounds`);
  }
  if (!deps.pickPuzzle && state.selectedCategoryIds.some((categoryId) => !getCategory(categoryId))) {
    throw new GameError('One or more categories are invalid');
  }

  return beginRound(
    {
      ...state,
      status: 'playing',
      currentRound: 0,
      currentPlayerIndex: 0,
      winners: [],
    },
    deps
  );
}

function beginRound(state: GameState, deps: EngineDeps): GameState {
  const roundNumber = state.currentRound + 1;
  const categoryId = state.selectedCategoryIds[roundNumber - 1];
  if (!categoryId) {
    throw new GameError('Missing category for this round');
  }

  const pick = (deps.pickPuzzle || defaultPickPuzzle)(categoryId, state.usedPuzzles);
  const starter = state.players[state.currentPlayerIndex];

  return {
    ...state,
    status: 'playing',
    phase: 'awaiting_action',
    currentRound: roundNumber,
    currentPhrase: pick.phrase,
    currentCategory: pick.categoryName,
    usedPuzzles: [...state.usedPuzzles, pick.phrase],
    guessedLetters: [],
    currentSpinValue: null,
    lastSpin: null,
    table: null,
    tableOffered: false,
    tableUsedThisTurn: false,
    players: clonePlayers(state.players).map((player) => ({ ...player, roundScore: 0 })),
    message: `Round ${roundNumber} of ${state.totalRounds}: ${pick.categoryName}. ${starter.name}'s turn — spin, buy a vowel for $${VOWEL_COST}, or solve.`,
  };
}

function spinSlot(state: GameState, playerId: string, spin: SpinResult, now: number): GameState {
  const player = requireTurn(state, playerId);
  if (state.phase !== 'awaiting_action') {
    throw new GameError('You already spun — guess a consonant');
  }
  if (!isValidSpin(spin)) {
    throw new GameError('Invalid spin result');
  }

  const afterSpin = {
    ...state,
    players: clonePlayers(state.players),
    lastSpin: spin,
    lastSpinAt: now,
    table: null,
  };

  if (spin.specialValue === 'BANKRUPT') {
    const players = afterSpin.players.map((entry) =>
      entry.id === player.id ? { ...entry, roundScore: 0 } : entry
    );
    return passTurn(
      {
        ...afterSpin,
        players,
        currentSpinValue: null,
      },
      `${player.name} landed on BANKRUPT and lost their round money!`
    );
  }

  if (spin.specialValue === 'LOSE TURN') {
    return passTurn(
      {
        ...afterSpin,
        currentSpinValue: null,
      },
      `${player.name} landed on LOSE TURN.`
    );
  }

  const forcedTable = spin.specialValue === 'TABLE' && !state.tableUsedThisTurn;
  if (forcedTable) {
    return {
      ...afterSpin,
      currentSpinValue: spin.cashValue,
      tableOffered: false,
      tableUsedThisTurn: true,
      phase: 'awaiting_table_choice',
      message: `${player.name} landed on TABLE. ${player.name} plays Table 21 for $${spin.cashValue}.`,
    };
  }

  const offerTable =
    spin.cashValue >= TABLE_PUSH_IT_MIN && !state.tableUsedThisTurn && spin.specialValue !== 'TABLE';

  const cashMessage =
    spin.specialValue === 'TABLE' && state.tableUsedThisTurn
      ? `${player.name} landed on TABLE, but already played Table 21 this turn. ${player.name} must pick a consonant for $${spin.cashValue}.`
      : offerTable
        ? `${player.name} spun $${spin.cashValue}. Push it at Table 21, or pick a consonant.`
        : `${player.name} spun $${spin.cashValue}. ${player.name} must pick a consonant.`;

  return {
    ...afterSpin,
    currentSpinValue: spin.cashValue,
    tableOffered: offerTable,
    phase: 'awaiting_consonant',
    message: cashMessage,
  };
}

function startTable(
  state: GameState,
  playerId: string,
  againstInput: TableAgainst,
  playerHandInput: TableCard[],
  opponentHandInput: TableCard[]
): GameState {
  const player = requireTurn(state, playerId);
  const optionalPush =
    state.phase === 'awaiting_consonant' &&
    Boolean(state.tableOffered) &&
    !state.tableUsedThisTurn &&
    (state.currentSpinValue ?? 0) >= TABLE_PUSH_IT_MIN;
  const forcedChoice = state.phase === 'awaiting_table_choice';

  if (!optionalPush && !forcedChoice) {
    throw new GameError('Table Challenge is not available');
  }
  if (state.currentSpinValue == null) {
    throw new GameError('There is no spin to take to the table');
  }

  const against = normalizeAgainst(state, player.id, againstInput);
  const playerHand = requireDealtHand(playerHandInput, 'your hand');
  const opponentHand = requireDealtHand(opponentHandInput, 'the other hand');
  if (!cardsAreUnique([...playerHand, ...opponentHand])) {
    throw new GameError('Invalid table cards');
  }

  const opponentName =
    against.type === 'player'
      ? state.players.find((entry) => entry.id === against.playerId)?.name
      : undefined;
  const table: TableChallenge = {
    playerId: player.id,
    against,
    stake: state.currentSpinValue,
    playerHand,
    opponentHand,
    holeHidden: against.type === 'house',
    outcome: null,
  };

  return {
    ...state,
    players: clonePlayers(state.players),
    table,
    tableOffered: false,
    tableUsedThisTurn: true,
    phase: 'awaiting_table',
    message:
      against.type === 'house'
        ? `${player.name} plays Table 21 against the house for $${table.stake}. Hit once or stand.`
        : `${player.name} plays Table 21 against ${opponentName} for $${table.stake}. Hit once or stand.`,
  };
}

function resolveTable(
  state: GameState,
  playerId: string,
  playerHitInput: TableCard | null,
  opponentHitsInput: TableCard[]
): GameState {
  const player = requireTurn(state, playerId);
  if (state.phase !== 'awaiting_table' || !state.table) {
    throw new GameError('No Table Challenge to resolve');
  }
  if (state.table.playerId !== player.id || state.table.outcome) {
    throw new GameError('This Table Challenge is already finished');
  }

  const playerHit = playerHitInput == null ? null : requireCard(playerHitInput, 'hit card');
  if (!Array.isArray(opponentHitsInput) || opponentHitsInput.some((card) => !isValidCard(card))) {
    throw new GameError('Invalid table cards');
  }
  const opponentHits = opponentHitsInput.filter(isValidCard);
  if (!opponentHitsAreLegal(state.table.opponentHand, opponentHits)) {
    throw new GameError('The other hand must stand on 17 and hit below 17');
  }

  const playerHand = playerHit ? [...state.table.playerHand, playerHit] : [...state.table.playerHand];
  const opponentHand = [...state.table.opponentHand, ...opponentHits];
  if (!cardsAreUnique([...playerHand, ...opponentHand])) {
    throw new GameError('Invalid table cards');
  }

  const outcome = tableOutcome(playerHand, opponentHand);
  const table: TableChallenge = {
    ...state.table,
    playerHand,
    opponentHand,
    holeHidden: false,
    outcome,
  };

  if (outcome === 'lose') {
    return passTurn(
      {
        ...state,
        table,
        currentSpinValue: null,
      },
      `${player.name} lost Table 21 and forfeited $${table.stake}.`
    );
  }

  const nextStake = outcome === 'win' ? table.stake * 2 : table.stake;
  return {
    ...state,
    players: clonePlayers(state.players),
    table,
    currentSpinValue: nextStake,
    tableOffered: false,
    tableUsedThisTurn: true,
    phase: 'awaiting_consonant',
    message:
      outcome === 'win'
        ? `${player.name} doubled to $${nextStake} at Table 21. Pick a consonant.`
        : `${player.name} pushed at Table 21. Stake stays $${nextStake}. Pick a consonant.`,
  };
}

function requireDealtHand(cards: TableCard[], label: string): [TableCard, TableCard] {
  if (!Array.isArray(cards) || cards.length !== 2 || !cards.every(isValidCard)) {
    throw new GameError(`Invalid ${label}`);
  }
  return [cards[0], cards[1]] as [TableCard, TableCard];
}

function requireCard(card: TableCard, label: string): TableCard {
  if (!isValidCard(card)) {
    throw new GameError(`Invalid ${label}`);
  }
  return { rank: card.rank, suit: card.suit };
}

function normalizeAgainst(state: GameState, playerId: string, against: TableAgainst): TableAgainst {
  if (!against || typeof against !== 'object') {
    throw new GameError('Pick the house or another player');
  }
  if (against.type === 'house') {
    return { type: 'house' };
  }
  if (against.type === 'player') {
    if (!against.playerId || against.playerId === playerId) {
      throw new GameError('Pick the house or another player');
    }
    const opponent = state.players.find((entry) => entry.id === against.playerId);
    if (!opponent) {
      throw new GameError('That player is not in this game');
    }
    return { type: 'player', playerId: opponent.id };
  }
  throw new GameError('Pick the house or another player');
}

function guessConsonant(state: GameState, playerId: string, letterInput: string): GameState {
  const player = requireTurn(state, playerId);
  if (state.phase !== 'awaiting_consonant' || state.currentSpinValue == null) {
    throw new GameError('Spin first, then guess a consonant');
  }

  const letter = normalizeLetter(letterInput);
  if (isVowel(letter)) {
    throw new GameError('Vowels must be bought — they are not free');
  }
  if (state.guessedLetters.includes(letter)) {
    throw new GameError(`"${letter.toUpperCase()}" has already been guessed`);
  }

  const count = countLetterInPhrase(state.currentPhrase, letter);
  const guessedLetters = [...state.guessedLetters, letter];

  if (count === 0) {
    return passTurn(
      {
        ...state,
        guessedLetters,
        currentSpinValue: null,
        table: null,
        tableOffered: false,
      },
      `${player.name} guessed ${letter.toUpperCase()} — not in the puzzle.`
    );
  }

  const points = count * state.currentSpinValue;
  const players = clonePlayers(state.players).map((entry) =>
    entry.id === player.id ? { ...entry, roundScore: entry.roundScore + points } : entry
  );
  const nextState: GameState = {
    ...state,
    players,
    guessedLetters,
    currentSpinValue: null,
    table: null,
    tableOffered: false,
    phase: 'awaiting_action',
    message: `${player.name} found ${count} ${letter.toUpperCase()}${count === 1 ? '' : 's'} for $${points}! Spin again, buy a vowel, or solve.`,
  };

  if (isBoardComplete(nextState.currentPhrase, nextState.guessedLetters)) {
    return completeRound(nextState, player.id, `${player.name} revealed the last letter`);
  }

  return nextState;
}

function buyVowel(state: GameState, playerId: string, letterInput: string): GameState {
  const player = requireTurn(state, playerId);
  if (state.phase !== 'awaiting_action') {
    throw new GameError('Finish your spin before buying a vowel');
  }

  const letter = normalizeLetter(letterInput);
  if (!isVowel(letter)) {
    throw new GameError('You can only buy A, E, I, O, or U');
  }
  if (state.guessedLetters.includes(letter)) {
    throw new GameError(`"${letter.toUpperCase()}" has already been guessed`);
  }
  if (player.roundScore < VOWEL_COST) {
    throw new GameError(`Vowels cost $${VOWEL_COST} in round money`);
  }

  const count = countLetterInPhrase(state.currentPhrase, letter);
  const guessedLetters = [...state.guessedLetters, letter];
  const players = clonePlayers(state.players).map((entry) =>
    entry.id === player.id ? { ...entry, roundScore: entry.roundScore - VOWEL_COST } : entry
  );

  if (count === 0) {
    return passTurn(
      {
        ...state,
        players,
        guessedLetters,
        currentSpinValue: null,
      },
      `${player.name} bought ${letter.toUpperCase()} for $${VOWEL_COST} — not in the puzzle.`
    );
  }

  const nextState: GameState = {
    ...state,
    players,
    guessedLetters,
    currentSpinValue: null,
    phase: 'awaiting_action',
    message: `${player.name} bought ${count} ${letter.toUpperCase()}${count === 1 ? '' : 's'} for $${VOWEL_COST}.`,
  };

  if (isBoardComplete(nextState.currentPhrase, nextState.guessedLetters)) {
    return completeRound(nextState, player.id, `${player.name} revealed the last letter`);
  }

  return nextState;
}

function solvePhrase(state: GameState, playerId: string, guess: string): GameState {
  const player = requireTurn(state, playerId);
  if (state.phase !== 'awaiting_action') {
    throw new GameError('Guess the consonant from your spin before solving');
  }
  if (!guess.trim()) {
    throw new GameError('Enter a solution');
  }

  const correct =
    normalizeSolveGuess(guess) === normalizeSolveGuess(state.currentPhrase);

  if (!correct) {
    return passTurn(
      {
        ...state,
        currentSpinValue: null,
      },
      `${player.name} tried to solve and was incorrect.`
    );
  }

  const revealed = Array.from(new Set([
    ...state.guessedLetters,
    ...uniqueLettersInPhrase(state.currentPhrase),
  ]));

  return completeRound(
    {
      ...state,
      guessedLetters: revealed,
      currentSpinValue: null,
    },
    player.id,
    `${player.name} solved the puzzle`
  );
}

function completeRound(state: GameState, winnerId: string, preface: string): GameState {
  const players = clonePlayers(state.players).map((player) => {
    if (player.id === winnerId) {
      return {
        ...player,
        totalScore: player.totalScore + player.roundScore,
        roundScore: 0,
      };
    }
    return { ...player, roundScore: 0 };
  });
  const banked = (state.players.find((player) => player.id === winnerId)?.roundScore ?? 0);
  const winnerIndex = players.findIndex((player) => player.id === winnerId);

  const revealed = Array.from(new Set([
    ...state.guessedLetters,
    ...uniqueLettersInPhrase(state.currentPhrase),
  ]));

  const base: GameState = {
    ...state,
    players,
    guessedLetters: revealed,
    currentPlayerIndex: winnerIndex >= 0 ? winnerIndex : state.currentPlayerIndex,
    currentSpinValue: null,
    table: null,
    tableOffered: false,
    tableUsedThisTurn: false,
    phase: 'round_complete',
    message: `${preface} and banked $${banked}! The puzzle was "${state.currentPhrase}".`,
  };

  if (state.currentRound >= state.totalRounds) {
    return finishGame(base, `${base.message} That's the final round.`);
  }

  return base;
}

function advanceRound(state: GameState, deps: EngineDeps): GameState {
  if (state.status === 'finished' || state.phase === 'game_over') {
    return state;
  }
  if (state.phase !== 'round_complete') {
    // Idempotent: every client may try to advance after a round.
    return state;
  }
  if (state.currentRound >= state.totalRounds) {
    return finishGame(state, state.message);
  }

  return beginRound(
    {
      ...state,
      currentPlayerIndex: nextIndex(state),
    },
    deps
  );
}

function endGame(state: GameState, playerId: string): GameState {
  requireHost(state, playerId);
  if (state.status === 'finished') {
    return state;
  }

  const players = clonePlayers(state.players).map((player) => ({
    ...player,
    totalScore: player.totalScore + player.roundScore,
    roundScore: 0,
  }));

  return finishGame(
    {
      ...state,
      players,
    },
    'The host ended the game.'
  );
}

function finishGame(state: GameState, preface: string): GameState {
  const highest = Math.max(0, ...state.players.map((player) => player.totalScore));
  const winners = state.players.filter((player) => player.totalScore === highest);
  const winnerText =
    winners.length === 1
      ? `${winners[0].name} wins with $${winners[0].totalScore}!`
      : `It's a tie at $${highest}: ${winners.map((player) => player.name).join(', ')}.`;

  return {
    ...state,
    status: 'finished',
    phase: 'game_over',
    currentSpinValue: null,
    table: null,
    tableOffered: false,
    tableUsedThisTurn: false,
    winners: clonePlayers(winners),
    message: `${preface} ${winnerText}`.trim(),
  };
}

export function displayCells(phrase: string, guessedLetters: string[]): Array<{
  char: string;
  revealed: boolean;
  isSpace: boolean;
  isPunctuation: boolean;
}> {
  const guessed = new Set(guessedLetters.map((letter) => letter.toLowerCase()));
  return phrase.split('').map((char) => {
    const isSpace = char === ' ';
    const isLetter = /[a-zA-Z]/.test(char);
    const revealed = !isLetter || guessed.has(char.toLowerCase());
    return {
      char,
      revealed,
      isSpace,
      isPunctuation: !isSpace && !isLetter,
    };
  });
}
